<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">{{ isEditMode ? 'Edit Distribution' : 'Create Distribution' }}</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <form @submit.prevent="onSubmit">
        <!-- Distribution Name -->
        <div class="mb-3">
          <label class="form-label">Distribution Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="formData.distributionName"
            placeholder="Enter distribution name"
            required
            :disabled="isEditMode"
          />
        </div>

        <!-- Destination Platform -->
        <div class="mb-3">
          <label class="form-label">Destination Platform <span class="text-danger">*</span></label>
          <div class="row g-2">
            <div class="col-6 col-md-4 col-lg-3" v-for="dest in destinationTiles" :key="dest.value">
              <button
                type="button"
                class="destination-tile"
                :class="{ active: formData.destinationName === dest.value }"
                @click="formData.destinationName = dest.value"
              >
                <span
                  class="destination-tile-icon"
                  :class="{ 'destination-tile-icon-img': dest.img }"
                  :style="{ backgroundColor: dest.img ? 'transparent' : dest.color }"
                >
                  <img
                    v-if="dest.img"
                    :src="dest.img"
                    :alt="dest.label"
                    :class="{ 'destination-tile-icon-img-contain': dest.imgFit === 'contain' }"
                  />
                  <i v-else-if="dest.icon" :class="dest.icon"></i>
                  <span v-else class="destination-tile-icon-text">{{ dest.iconText }}</span>
                </span>
                <span class="destination-tile-label">{{ dest.label }}</span>
                <span class="destination-tile-sublabel">{{ dest.sublabel }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Entity Types -->
        <div class="mb-3">
          <label class="form-label">Entity Types</label>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="maids" v-model="formData.entityTypes.maids" />
            <label class="form-check-label" for="maids">MAIDs</label>
          </div>
        </div>

        <!-- Schedule -->
        <div class="mb-3">
          <label class="form-label">Schedule <span class="text-danger">*</span></label>
          <select class="form-select" v-model="formData.scheduleType">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <small class="text-muted">
            Cron Expression: <code>{{ fullSchedule }}</code>
          </small>
        </div>

        <!-- Date Range -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Start Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" v-model="formData.startDate" required />
          </div>
          <div class="col-md-6">
            <label class="form-label">End Date <span class="text-danger">*</span></label>
            <input type="date" class="form-control" v-model="formData.endDate" required />
          </div>
        </div>

        <!-- Advanced Options -->
        <div class="mb-3">
          <a href="#" class="text-decoration-none" @click.prevent="showAdvanced = !showAdvanced">
            <i :class="showAdvanced ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="me-2"></i>
            Advanced Options
          </a>
          <div v-if="showAdvanced" class="mt-3 p-3 bg-light rounded">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Lookback Window (days)</label>
                <input type="number" class="form-control" v-model.number="formData.lookbackWindowDays" min="1" />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Backfill Period (days)</label>
                <input type="number" class="form-control" v-model.number="formData.backfillPeriodDays" min="1" />
              </div>
            </div>
          </div>
        </div>
      </form>

      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
    </div>
    <div class="card-footer">
      <button
        type="button"
        class="btn btn-primary"
        @click="onSubmit"
        :disabled="!isFormValid || saving"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-2" />
        {{ isEditMode ? 'Update' : 'Create' }} Distribution
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { DISTRIBUTION_DESTINATIONS, getDistributionDestinationDisplay } from '../../utils/field-constants/distributionDestinations'

export default {
  name: 'AddDistribution',
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      showAdvanced: false,
      destinations: [],
      formData: {
        distributionName: '',
        destinationName: '',
        entityTypes: { maids: true },
        scheduleType: 'daily',
        startDate: '',
        endDate: '',
        lookbackWindowDays: 1,
        backfillPeriodDays: 1,
      },
    }
  },
  computed: {
    isEditMode() {
      return this.modalData?.distribution != null
    },
    audienceName() {
      return this.modalData?.audienceName
    },
    isFormValid() {
      return (
        this.formData.distributionName.trim() !== '' &&
        this.formData.destinationName !== '' &&
        this.formData.startDate !== '' &&
        this.formData.endDate !== ''
      )
    },
    fullSchedule() {
      const scheduleMap = {
        daily: '* * * * *',
        weekly: '* * * * 0',
        monthly: '* * 1 * *',
      }
      return scheduleMap[this.formData.scheduleType] || '* * * * *'
    },
    destinationTiles() {
      const knownValues = new Set(DISTRIBUTION_DESTINATIONS.map((dest) => dest.value))
      const tiles = [...DISTRIBUTION_DESTINATIONS]
      this.destinations
        .map((dest) => dest.destination_name)
        .concat(this.formData.destinationName ? [this.formData.destinationName] : [])
        .forEach((value) => {
          if (!value || knownValues.has(value)) return
          tiles.push(getDistributionDestinationDisplay(value))
          knownValues.add(value)
        })

      return tiles
    },
  },
  methods: {
    ...mapActions({
      fetchDistributionDestinations: 'audience/fetchDistributionDestinations',
      createDistribution: 'audience/createDistribution',
      updateDistribution: 'audience/updateDistribution',
      hideModal: 'hideModal',
      addToast: 'addToast',
    }),
    async loadDestinations() {
      this.loading = true
      try {
        this.destinations = await this.fetchDistributionDestinations()
      } catch (error) {
        this.error = 'Failed to load destinations'
      } finally {
        this.loading = false
      }
    },
    populateFormFromDistribution() {
      if (this.modalData?.distribution) {
        const dist = this.modalData.distribution
        this.formData.distributionName = dist.distribution_name || ''
        this.formData.destinationName = dist.destination_name || dist.Destination_name || ''
        this.formData.startDate = dist.start_date || ''
        this.formData.endDate = dist.end_date || ''
        this.formData.lookbackWindowDays = dist.lookback_window_days || 1
        this.formData.backfillPeriodDays = dist.backfill_period_days || 1

        const entityTypes = dist.entity_types || ''
        this.formData.entityTypes.maids = entityTypes.toLowerCase().includes('maid')

        // Parse schedule
        const schedule = dist.schedule || ''
        if (schedule.includes('0') && schedule.split(' ')[4] === '0') {
          this.formData.scheduleType = 'weekly'
        } else if (schedule.includes('1') && schedule.split(' ')[2] === '1') {
          this.formData.scheduleType = 'monthly'
        } else {
          this.formData.scheduleType = 'daily'
        }
      }
    },
    async onSubmit() {
      if (!this.isFormValid) return

      this.saving = true
      this.error = null

      try {
        const entityTypes = this.formData.entityTypes.maids ? 'MAIDs' : ''

        const distributionData = {
          audience_name: this.audienceName,
          destination_name: this.formData.destinationName,
          distribution_name: this.formData.distributionName,
          entity_types: entityTypes,
          lookback_window_days: this.formData.lookbackWindowDays,
          backfill_period_days: this.formData.backfillPeriodDays,
          schedule: this.fullSchedule,
          start_date: this.formData.startDate,
          end_date: this.formData.endDate,
        }

        if (this.isEditMode) {
          await this.updateDistribution({
            audienceName: this.audienceName,
            distributionName: this.modalData.distribution.distribution_name,
            distributionData,
          })
          this.addToast({ type: 'success', message: 'Distribution updated successfully' })
        } else {
          await this.createDistribution({
            audienceName: this.audienceName,
            distributionData,
          })
          this.addToast({ type: 'success', message: 'Distribution created successfully' })
        }

        this.hideModal()
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to save distribution'
      } finally {
        this.saving = false
      }
    },
  },
  mounted() {
    this.loadDestinations()
    if (this.isEditMode) {
      this.populateFormFromDistribution()
    }
  },
}
</script>

<style scoped>
.destination-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: center;
}

.destination-tile:hover {
  border-color: #adb5bd;
}

.destination-tile.active {
  border-color: #0d6efd;
  box-shadow: 0 0 0 1px #0d6efd;
  background-color: #f0f6ff;
}

.destination-tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  color: #fff;
  font-size: 1.1rem;
  margin-bottom: 0.4rem;
  overflow: hidden;
}

.destination-tile-icon-img {
  border: 1px solid #dee2e6;
}

.destination-tile-icon-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.destination-tile-icon-img img.destination-tile-icon-img-contain {
  padding: 15%;
  box-sizing: border-box;
  object-fit: contain;
}

.destination-tile-icon-text {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
}

.destination-tile-label {
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.1;
}

.destination-tile-sublabel {
  font-size: 0.7rem;
  color: #6c757d;
  line-height: 1.1;
  margin-top: 0.15rem;
}
</style>
